/**
 * EE AI 蒸餾審核頁（Phase 8） — admin only menu
 * func_code: ee_ai_review (is_mana=true)
 *
 * 平台 admin review usage_logs 全文 → 標 good / bad / edited → 匯出 JSONL → LoRA 訓練自家 Ollama
 *
 * 商業意義：admin 用自家 Claude key 跑 → usage_logs 全文落地 → 自審 → 蒸餾 → 自家 Ollama 越來越準
 *           其他租戶看不到此頁（is_mana=true gate）— 是 admin 的競爭力護城河
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Stack, Button, Chip, IconButton, Tooltip, Typography,
  Paper, CircularProgress, Alert, TextField,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions,
  MenuItem, Select, FormControl, InputLabel,
  Collapse,
} from '@mui/material';
import {
  Refresh as RefreshIcon, Download as DownloadIcon,
  ThumbUp as GoodIcon, ThumbDown as BadIcon, Edit as EditIcon,
  ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon,
  Science as ReviewIcon,
} from '@mui/icons-material';

import FunctionPageHeader from '../../components/FunctionPageHeader';
import { useMessage } from '../../contexts/MessageContext';
import { logCreate, logDelete, logView } from '../../utils/userLogHelper';
import {
  AiUsageLog, listAiUsageLogs,
  UsageLogFull, QualityLabel,
  getUsageLogFull, reviewTrainingSample, exportTrainingJsonlUrl,
} from '../../services/eeAiService';
import { formatLocal } from '../../utils/dateHelper';

const FUNC_CODE = 'ee_ai_review';

const fmt = (iso?: string | null) => iso ? formatLocal(iso) : '—';

const QualityChip: React.FC<{ label?: QualityLabel | null }> = ({ label }) => {
  if (!label) return <Chip label="未審" size="small" variant="outlined" />;
  const colorMap: Record<QualityLabel, 'success' | 'error' | 'warning'> = {
    good: 'success', bad: 'error', edited: 'warning',
  };
  const labelMap: Record<QualityLabel, string> = {
    good: '👍 Good', bad: '👎 Bad', edited: '✏️ Edited',
  };
  return <Chip label={labelMap[label]} size="small" color={colorMap[label]} />;
};

const EeAiReviewPage: React.FC = () => {
  const { showSuccess, showApiError } = useMessage();

  const [logs, setLogs] = useState<AiUsageLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [filterFeature, setFilterFeature] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('success');

  // 全文展開
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [fullCache, setFullCache] = useState<Record<number, UsageLogFull>>({});
  const [loadingFull, setLoadingFull] = useState<number | null>(null);

  // 編輯模式
  const [editTarget, setEditTarget] = useState<UsageLogFull | null>(null);
  const [editOutput, setEditOutput] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listAiUsageLogs({
        page: 1, page_size: 100,
        status: filterStatus || undefined,
      });
      setLogs(data.items);
      logView(FUNC_CODE, { count: data.items.length, status: filterStatus });
    } catch (err: any) {
      showApiError(err);
    } finally {
      setLoading(false);
    }
  }, [filterStatus, showApiError]);

  useEffect(() => { reload(); }, [reload]);

  const handleExpand = async (logId: number) => {
    if (expandedId === logId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(logId);
    if (!fullCache[logId]) {
      setLoadingFull(logId);
      try {
        const full = await getUsageLogFull(logId);
        setFullCache(prev => ({ ...prev, [logId]: full }));
      } catch (err: any) {
        showApiError(err);
        setExpandedId(null);
      } finally {
        setLoadingFull(null);
      }
    }
  };

  const handleQuickReview = async (logId: number, label: QualityLabel) => {
    try {
      await reviewTrainingSample({ usage_log_id: logId, quality_label: label });
      logCreate(FUNC_CODE, { action: 'review', usage_log_id: logId, label });
      showSuccess('SYS020001', { name: `已標 ${label}` });
      // 更新本機 cache
      setFullCache(prev => {
        const cur = prev[logId];
        if (!cur) return prev;
        return { ...prev, [logId]: { ...cur, quality_label: label, sample_id: cur.sample_id || -1 } };
      });
    } catch (err: any) {
      logDelete(FUNC_CODE, { logId, label }, String(err?.message || err));
      showApiError(err);
    }
  };

  const openEditDialog = (full: UsageLogFull) => {
    setEditTarget(full);
    setEditOutput(full.corrected_output || full.output_full || '');
    setEditNotes(full.notes || '');
  };

  const handleSaveEdit = async () => {
    if (!editTarget) return;
    setEditSaving(true);
    try {
      await reviewTrainingSample({
        usage_log_id: editTarget.id,
        quality_label: 'edited',
        corrected_output: editOutput,
        notes: editNotes || null,
      });
      showSuccess('SYS020002', { name: 'edited 修改', id: String(editTarget.id) });
      setFullCache(prev => ({
        ...prev,
        [editTarget.id]: {
          ...editTarget,
          quality_label: 'edited',
          corrected_output: editOutput,
          notes: editNotes,
          sample_id: editTarget.sample_id || -1,
        },
      }));
      setEditTarget(null);
    } catch (err: any) {
      showApiError(err);
    } finally {
      setEditSaving(false);
    }
  };

  const handleDownloadJsonl = (label: 'good' | 'edited' | 'all') => {
    const url = exportTrainingJsonlUrl(label, filterFeature || undefined);
    // 直接觸發下載
    const a = document.createElement('a');
    a.href = url;
    a.download = `training_${label}.jsonl`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const filteredLogs = logs.filter(l =>
    !filterFeature || l.feature_code === filterFeature,
  );

  // 收集出現過的 feature_codes 給 filter 下拉
  const featureCodes = Array.from(new Set(
    logs.map(l => l.feature_code).filter(Boolean) as string[],
  ));

  return (
    <Box>
      <FunctionPageHeader funcCode={FUNC_CODE} fallback="AI 蒸餾審核" />

      <Alert severity="info" sx={{ mt: 2, mb: 2 }} icon={<ReviewIcon />}>
        平台管理員審核 LLM 用量全文 → 標 good / bad / edited → 匯出 JSONL → LoRA 訓練自家 Ollama。
        <br />
        ⚠️ 此頁僅平台管理員可見（is_mana=true gate） — 商業隔離；其他租戶看不到 admin 的 prompt 過程。
      </Alert>

      <Stack direction="row" spacing={1} sx={{ mb: 2 }} alignItems="center">
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>狀態</InputLabel>
          <Select
            label="狀態" value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
          >
            <MenuItem value="success">success</MenuItem>
            <MenuItem value="failed">failed</MenuItem>
            <MenuItem value="">全部</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Feature</InputLabel>
          <Select
            label="Feature" value={filterFeature}
            onChange={e => setFilterFeature(e.target.value)}
          >
            <MenuItem value="">（全部）</MenuItem>
            {featureCodes.map(f => (
              <MenuItem key={f} value={f}>{f}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button size="small" variant="outlined"
                startIcon={loading ? <CircularProgress size={14} /> : <RefreshIcon />}
                onClick={reload} disabled={loading}>重新整理</Button>

        <Box sx={{ flex: 1 }} />

        <Tooltip title="匯出已標 good 的 sample">
          <Button size="small" variant="contained" color="success"
                  startIcon={<DownloadIcon />}
                  onClick={() => handleDownloadJsonl('good')}>
            匯出 good JSONL
          </Button>
        </Tooltip>
        <Tooltip title="匯出 good + edited 兩類">
          <Button size="small" variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => handleDownloadJsonl('all')}>
            匯出 all
          </Button>
        </Tooltip>
      </Stack>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell width={40}></TableCell>
              <TableCell width={70}>ID</TableCell>
              <TableCell width={170}>時間</TableCell>
              <TableCell width={160}>Feature</TableCell>
              <TableCell width={150}>Provider / Model</TableCell>
              <TableCell width={70}>Tokens</TableCell>
              <TableCell width={80}>狀態</TableCell>
              <TableCell width={100}>審核</TableCell>
              <TableCell>Prompt 預覽</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredLogs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  {loading ? '載入中…' : '無符合的紀錄'}
                </TableCell>
              </TableRow>
            ) : filteredLogs.map(l => {
              const expanded = expandedId === l.id;
              const full = fullCache[l.id];
              return (
                <React.Fragment key={l.id}>
                  <TableRow hover>
                    <TableCell>
                      <IconButton size="small" onClick={() => handleExpand(l.id)}>
                        {expanded ? <ExpandLessIcon fontSize="small" />
                          : <ExpandMoreIcon fontSize="small" />}
                      </IconButton>
                    </TableCell>
                    <TableCell>{l.id}</TableCell>
                    <TableCell>
                      <Typography variant="caption">{fmt(l.created_at)}</Typography>
                    </TableCell>
                    <TableCell>
                      {l.feature_code
                        ? <Chip size="small" label={l.feature_code} variant="outlined" />
                        : <Typography variant="caption" color="text.disabled">—</Typography>}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ display: 'block', fontFamily: 'monospace' }}>
                        {l.provider_code}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {l.model_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {l.total_tokens || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={l.status}
                            color={l.status === 'success' ? 'success' : 'error'} />
                    </TableCell>
                    <TableCell>
                      <QualityChip label={full?.quality_label} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: 400,
                      }}>
                        {l.prompt_preview || '—'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={9} sx={{ p: 0, borderBottom: expanded ? undefined : 0 }}>
                      <Collapse in={expanded} timeout="auto" unmountOnExit>
                        <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                          {loadingFull === l.id ? (
                            <Box sx={{ textAlign: 'center', py: 2 }}>
                              <CircularProgress size={24} />
                            </Box>
                          ) : full ? (
                            <Stack spacing={2}>
                              {full.prompt_full && (
                                <Box>
                                  <Typography variant="caption" color="primary"
                                              sx={{ fontWeight: 600 }}>
                                    📥 PROMPT_FULL（{full.prompt_full.length} 字）
                                  </Typography>
                                  <Paper variant="outlined" sx={{
                                    p: 1, mt: 0.5,
                                    maxHeight: 240, overflow: 'auto',
                                    fontFamily: 'monospace',
                                    fontSize: '0.78rem',
                                    whiteSpace: 'pre-wrap',
                                  }}>
                                    {full.prompt_full}
                                  </Paper>
                                </Box>
                              )}
                              {full.output_full && (
                                <Box>
                                  <Typography variant="caption" color="secondary"
                                              sx={{ fontWeight: 600 }}>
                                    📤 OUTPUT_FULL（{full.output_full.length} 字）
                                  </Typography>
                                  <Paper variant="outlined" sx={{
                                    p: 1, mt: 0.5,
                                    maxHeight: 240, overflow: 'auto',
                                    fontFamily: 'monospace',
                                    fontSize: '0.78rem',
                                    whiteSpace: 'pre-wrap',
                                  }}>
                                    {full.output_full}
                                  </Paper>
                                </Box>
                              )}
                              {full.corrected_output && (
                                <Box>
                                  <Typography variant="caption" color="warning.main"
                                              sx={{ fontWeight: 600 }}>
                                    ✏️ CORRECTED_OUTPUT
                                  </Typography>
                                  <Paper variant="outlined" sx={{
                                    p: 1, mt: 0.5,
                                    maxHeight: 240, overflow: 'auto',
                                    fontFamily: 'monospace',
                                    fontSize: '0.78rem',
                                    whiteSpace: 'pre-wrap',
                                    bgcolor: 'warning.50',
                                  }}>
                                    {full.corrected_output}
                                  </Paper>
                                </Box>
                              )}
                              {/* 審核按鈕 */}
                              <Stack direction="row" spacing={1}>
                                <Button size="small" variant="contained" color="success"
                                        startIcon={<GoodIcon />}
                                        onClick={() => handleQuickReview(l.id, 'good')}>
                                  Good
                                </Button>
                                <Button size="small" variant="contained" color="error"
                                        startIcon={<BadIcon />}
                                        onClick={() => handleQuickReview(l.id, 'bad')}>
                                  Bad
                                </Button>
                                <Button size="small" variant="outlined" color="warning"
                                        startIcon={<EditIcon />}
                                        onClick={() => openEditDialog(full)}>
                                  Edit (修改 output)
                                </Button>
                                {full.quality_label && (
                                  <Typography variant="caption" sx={{ alignSelf: 'center', ml: 2 }}
                                              color="text.secondary">
                                    當前審核：<QualityChip label={full.quality_label} />
                                  </Typography>
                                )}
                              </Stack>
                            </Stack>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              （展開以載入全文）
                            </Typography>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 編輯 dialog */}
      <Dialog open={!!editTarget} onClose={() => setEditTarget(null)}
              maxWidth="md" fullWidth>
        <DialogTitle>修改 output（標 edited，匯入訓練集會用 corrected_output）</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="修改後的 output（會取代原 LLM 輸出進蒸餾）"
              multiline rows={12}
              value={editOutput}
              onChange={e => setEditOutput(e.target.value)}
              fullWidth
              inputProps={{ style: { fontFamily: 'monospace', fontSize: '0.85rem' } }}
            />
            <TextField
              label="審核註記（選填）"
              multiline rows={2}
              value={editNotes}
              onChange={e => setEditNotes(e.target.value)}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTarget(null)} disabled={editSaving}>取消</Button>
          <Button onClick={handleSaveEdit} variant="contained" color="warning"
                  disabled={editSaving || !editOutput.trim()}
                  startIcon={editSaving ? <CircularProgress size={14} /> : <EditIcon />}>
            儲存為 edited
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EeAiReviewPage;